<template>
  <div>
    <component
      :is="tableComponent"
      ref="table"
      v-bind="{...$props, ...$attrs}"
      v-on="$listeners"
    >
      <template slot="settingsControls">
        <slot name="settingsControls" />
      </template>
      <template slot="default">
        <slot />
      </template>
      <template
        #dropdown="props"
      >
        <slot
          v-bind="props"
          name="dropdown"
        />
      </template>
    </component>
    <Pagination
      v-if="showPagination && tableNotEmpty"
      :per-page="perPageValue"
      :per-page-choices="perPageChoices"
      :items-total="itemsTotal"
      :current-page="currentPageValue"
      :table-hint-text="tableHintText"
      @changePage="changePage"
      @changePerPage="changePerPage"
    />
    <div
      v-if="!tableNotEmpty && showEmptyMessage"
      class="empty-pagination-label"
    >
      {{ _getTr('pagination_no_items') }}
    </div>
  </div>
</template>

<script>/**
 * @exports PaginatedTable
 * @class PaginatedTable
 *
 * Wrap around {@link MTable} subclassed components. Add {@link Pagination} to table bottom
 *
 * @emits 'fetch' when pagination settings changed.
 * @type {{ page: number, perPage: number }}
 */

  import MTable from 'components/Data/Tables/MTable/MTable';
  import Pagination from 'components/Data/Pagination/Pagination';

  export default {
    name: 'PaginatedTable',
    components: {MTable, Pagination},
    props: {
      tableComponent: {
        type: Object,
        default: () => MTable
      },
      columns: {
        type: Array,
        default: Array
      },
      data: {
        type: Array,
        default: Array
      },
      itemsTotal: {
        type: Number,
        required: true
      },
      perPage: {
        type: Number,
        default: () => 20
      },
      currentPage: {
        type: Number,
        default: 1
      },
      tableWithSettings: {
        type: Boolean,
        default: false
      },
      params: {
        type: Object,
        default: Object
      },
      showPagination: {
        type: Boolean,
        default: true
      },
      initialSorting: {
        type: Array,
        default: Array
      },
      allowHeaderWordWrap: {
        type: Boolean,
        default: false
      },
      isDropdownEnabledPredicate: {
        type: Function,
        default: () => true
      },
      colorFunction: {
        type: Function,
        default: null
      },
      fitContainer: Boolean,
      fillContainer: Boolean,
      selectedIds: {
        type: Array,
        default: Array
      },
      fontSize: {
        type: String,
        default: 'S'
      },
      tableHintText: {
        type: String,
        default: ''
      },
      showEmptyMessage: {
        type: Boolean,
        default: true
      },
      perPageChoices: {
        type: Array,
        default: () => [10, 20, 50, 100]
      },
      multiSortable: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        currentPageValue: this.currentPage,
        perPageValue: this.perPage
      };
    },
    computed: {
      tableNotEmpty () {
        return this.itemsTotal !== 0;
      }
    },
    watch: {
      currentPage (val) {
        this.currentPageValue = val;
      },
      perPage (val) {
        this.perPageValue = val;
      },
      currentPageValue (val) {
        this.$emit('changePage', val);
      },
      perPageValue (val) {
        this.$emit('changePerPage', val);
      }
    },
    methods: {
      changePage (number) {
        this.currentPageValue = number;
        this.fetch();
      },
      changePerPage (perPage) {
        this.perPageValue = perPage;
        this.currentPageValue = 1;
        this.$nextTick(() => this.fetch());
      },
      fetch () {
        this.$emit('fetch', {page: this.currentPageValue, perPage: this.perPageValue});
      }
    }
  };
</script>

<style>
  .empty-pagination-label {
    margin-top: 10px;
  }
</style>
